import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ArrowSquareOutIcon,
  CheckCircleIcon,
  CopyIcon,
  GiftIcon,
  SpinnerGapIcon,
  TicketIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useToaster } from '../../../shared/ui';
import { rewardService } from '../../../shared/utils';
import type { UserReward } from '../../../shared/utils/rewardTypes';
import { Breadcrumb } from '../components/Breadcrumb';
import { formatCouponValue, formatDate } from '../utils/format';
import './RewardsHistoryPage.css';

export const RewardsHistoryPage = ({ paymentId }: { paymentId?: string }) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rewards, setRewards] = useState<UserReward[]>([]);
  const lang = i18n.language;

  const displayedRewards = paymentId
    ? rewards.filter(r => r.paymentId === paymentId)
    : rewards;

  useEffect(() => {
    const fetchRewardHistory = async () => {
      try {
        const response = await rewardService.getRewardHistory();

        if (response.success && response.data) {
          setRewards(response.data);
        } else {
          setError(response.error || t('rewardsHistory.failedToLoadError'));
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : t('rewardsHistory.genericError');
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchRewardHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAsUsed = async (rewardId: string) => {
    try {
      const response = await rewardService.markRewardAsUsed(rewardId);

      if (response.success) {
        showToast(t('rewardsHistory.markedAsUsed'), 'success');
        setRewards(prev =>
          prev.map(r =>
            r.id === rewardId
              ? { ...r, isUsed: true, usedAt: new Date().toISOString() }
              : r
          )
        );
      } else {
        showToast(
          response.error || t('rewardsHistory.markAsUsedError'),
          'error'
        );
      }
    } catch (err) {
      const apiErr = err as { message?: string };
      showToast(
        apiErr?.message || t('rewardsHistory.markAsUsedError'),
        'error'
      );
    }
  };

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showToast(t('rewardsHistory.copied'), 'success');
    } catch {
      showToast(value, 'info');
    }
  };

  const breadcrumb = <Breadcrumb items={[{ label: t('app.nav.rewards') }]} />;
  const heading = (
    <div className="mc-heading">
      <div>
        <h1>{t('rewardsHistory.heading')}</h1>
        <p>{t('rewardsHistory.headingSub')}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        {breadcrumb}
        {heading}
        <section className="mc-card mc-state">
          <div className="mc-state__icon">
            <SpinnerGapIcon size={46} weight="bold" className="mc-spin" />
          </div>
          <h2>{t('rewardsHistory.loadingTitle')}</h2>
          <p>{t('rewardsHistory.loadingMessage')}</p>
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        {breadcrumb}
        {heading}
        <section className="mc-card mc-state">
          <div className="mc-state__icon mc-state__icon--danger">
            <WarningCircleIcon size={46} weight="fill" />
          </div>
          <h2>{t('rewardsHistory.errorTitle')}</h2>
          <p>{error}</p>
          <div className="mc-actions">
            <Link to="/" className="mc-btn mc-btn--outline">
              {t('rewardsHistory.goToDashboard')}
            </Link>
          </div>
        </section>
      </>
    );
  }

  const availableCount = displayedRewards.filter(
    r => !r.isUsed && !r.isExpired
  ).length;
  const usedCount = displayedRewards.filter(r => r.isUsed).length;

  const metrics = [
    {
      icon: <TicketIcon size={27} weight="duotone" />,
      value: displayedRewards.length,
      label: t('rewardsHistory.totalRewards'),
    },
    {
      icon: <GiftIcon size={27} weight="duotone" />,
      value: availableCount,
      label: t('rewardsHistory.available'),
    },
    {
      icon: <CheckCircleIcon size={27} weight="duotone" />,
      value: usedCount,
      label: t('rewardsHistory.used'),
    },
  ];

  return (
    <>
      {breadcrumb}
      {heading}

      <div className="mc-grid mc-cols3">
        {metrics.map(metric => (
          <section key={metric.label} className="mc-card">
            <div className="mc-metric">
              <span className="mc-metric__symbol">{metric.icon}</span>
              <div>
                <b>{metric.value}</b>
                <span>{metric.label}</span>
              </div>
            </div>
          </section>
        ))}
      </div>

      {displayedRewards.length === 0 ? (
        <section className="mc-card mc-state">
          <div className="mc-state__icon">
            <GiftIcon size={46} weight="duotone" />
          </div>
          <h2>{t('rewardsHistory.noRewardsTitle')}</h2>
          <p>{t('rewardsHistory.noRewardsMessage')}</p>
          <div className="mc-actions">
            <Link to="/pay-rent" className="mc-btn mc-btn--primary">
              {t('rewardsHistory.payRentCta')}
            </Link>
          </div>
        </section>
      ) : (
        displayedRewards.map(reward => {
          const coupon = reward.coupon;
          const isAvailable = !reward.isUsed && !reward.isExpired;
          const offerLink =
            coupon?.externalLink && reward.couponCode
              ? coupon.externalLink.replace(
                  '{{coupon-code}}',
                  reward.couponCode
                )
              : undefined;

          return (
            <section key={reward.id} className="mc-card reward-item">
              <div className="mc-coupon">
                <div>
                  <span
                    className={`mc-tag ${
                      reward.isExpired
                        ? 'mc-tag--danger'
                        : reward.isUsed
                          ? 'mc-tag--neutral'
                          : 'mc-tag--success'
                    }`}
                  >
                    {reward.isExpired
                      ? t('rewardsHistory.expired')
                      : reward.isUsed
                        ? t('rewardsHistory.used')
                        : t('rewardsHistory.available')}
                  </span>
                  <h3 className="reward-item__title">
                    {coupon?.name || t('rewardsHistory.reward')}
                  </h3>
                  <p>
                    {[coupon?.merchantName, coupon?.description]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {coupon && (
                    <span className="mc-discount">
                      {formatCouponValue(coupon, t)}
                    </span>
                  )}
                </div>
                <GiftIcon
                  size={70}
                  weight="duotone"
                  className="mc-coupon__art"
                />
              </div>

              <div className="mc-grid mc-cols2 reward-item__details">
                {reward.couponCode && (
                  <div>
                    <span className="mc-muted mc-small">
                      {t('rewardsHistory.couponCode')}
                    </span>
                    <div className="mc-row mc-callout reward-item__code">
                      <strong>{reward.couponCode}</strong>
                      <button
                        type="button"
                        className="mc-btn mc-btn--soft mc-btn--small"
                        onClick={() => copyText(reward.couponCode!)}
                      >
                        <CopyIcon size={16} />
                        {t('rewardsHistory.copyCode')}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  {coupon && (
                    <>
                      <span className="mc-muted mc-small">
                        {t('rewardsHistory.validUntilLabel')}
                      </span>
                      <p className="reward-item__date">
                        <strong>{formatDate(coupon.validUntil, lang)}</strong>
                      </p>
                    </>
                  )}
                  <span className="mc-muted mc-small">
                    {t('rewardsHistory.claimedOn')}:{' '}
                    {formatDate(reward.claimedAt, lang)}
                    {reward.usedAt &&
                      ` · ${t('rewardsHistory.usedOn')}: ${formatDate(reward.usedAt, lang)}`}
                  </span>
                </div>
              </div>

              {(offerLink || isAvailable) && (
                <div className="mc-actions reward-item__actions">
                  {offerLink && (
                    <>
                      <a
                        className="mc-btn mc-btn--outline"
                        href={offerLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ArrowSquareOutIcon size={18} />
                        {t('rewardsHistory.openOffer')}
                      </a>
                      <button
                        type="button"
                        className="mc-btn mc-btn--outline"
                        onClick={() => copyText(offerLink)}
                      >
                        <CopyIcon size={18} />
                        {t('rewardsHistory.copyLink')}
                      </button>
                    </>
                  )}
                  {isAvailable && (
                    <button
                      type="button"
                      className="mc-btn mc-btn--primary"
                      onClick={() => handleMarkAsUsed(reward.id)}
                    >
                      {t('rewardsHistory.markAsUsed')}
                    </button>
                  )}
                </div>
              )}
            </section>
          );
        })
      )}
    </>
  );
};
